import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useCallDrawerStore } from "../stores/call-drawer-store";
import { verbeterApi } from "../lib/verbeterlus-api";
import {
  CATEGORIEEN,
  PLANVELDEN,
  STATUSSEN,
  VOLGENDE,
  type Melding,
  type Verbeterpunt,
} from "../lib/verbeterlus";

export function AgentVerbeteringenPage() {
  const [params, setParams] = useSearchParams(),
    id = params.get("id");
  const request = useRef(0),
    currentId = useRef(id);
  currentId.current = id;
  const opgeslagenStatus = useRef<Verbeterpunt["status"]>("nieuw");
  const [punten, setPunten] = useState<
      (Verbeterpunt & { agent_meldingen: { count: number }[] })[]
    >([]),
    [punt, setPunt] = useState<Verbeterpunt | null>(null),
    [meldingen, setMeldingen] = useState<Melding[]>([]);
  const [page, setPage] = useState(0),
    [totaal, setTotaal] = useState(0),
    [status, setStatus] = useState(""),
    [admin, setAdmin] = useState(false),
    [enabled, setEnabled] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const [historie, setHistorie] = useState<
    {
      id: number;
      actor: string;
      created_at: string;
      nieuw: { status: string; terugkoppeling: string };
    }[]
  >([]);
  async function load() {
    const ticket = ++request.current;
    setError("");
    try {
      const d = await verbeterApi(
        id ? `?id=${id}` : `?page=${page}&status=${status}`,
      );
      if (ticket !== request.current || currentId.current !== id) return;
      setEnabled(d.enabled);
      setAdmin(d.admin);
      if (id) {
        setPunt(d.punt);
        opgeslagenStatus.current = d.punt.status;
        setMeldingen(d.meldingen);
        setHistorie(d.historie);
      } else {
        setPunten(d.punten);
        setTotaal(d.totaal);
        setPunt(null);
      }
    } catch (e) {
      if (ticket === request.current)
        setError(e instanceof Error ? e.message : "Laden mislukt");
    }
  }
  useEffect(() => {
    setPunt(null);
    void load();
  }, [id, page, status]);
  async function mutate(body: Record<string, unknown>, method = "PATCH") {
    setBusy(true);
    setError("");
    try {
      await verbeterApi("", body, method);
      if (currentId.current === id) await load();
    } catch (e) {
      if (currentId.current === id)
        setError(e instanceof Error ? e.message : "Opslaan mislukt");
    } finally {
      setBusy(false);
    }
  }
  const field = (
    key: "titel" | "eigenaar" | "live_ref" | "controle_ref" | "terugkoppeling",
    label: string,
  ) => (
    <label className="block" key={key}>
      {label}
      <input
        className="block w-full border rounded p-2"
        value={punt?.[key] || ""}
        readOnly={!admin || busy || !enabled}
        onChange={(e) =>
          setPunt((p) => (p ? { ...p, [key]: e.target.value } : p))
        }
      />
    </label>
  );
  return (
    <div className="p-6 overflow-auto h-full space-y-4 max-w-5xl">
      <h1 className="text-xl font-semibold">Agent-verbeteringen</h1>
      <p>
        Voice en mail. Een afgehandeld klantgeval betekent niet dat de
        systeemfout is opgelost.
      </p>
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
      {!enabled && (
        <p className="text-amber-700">
          Melden en wijzigen staan uit. Bestaande punten blijven leesbaar.
        </p>
      )}
      {!id ? (
        <>
          <label>
            Status{" "}
            <select
              className="border p-2"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(0);
              }}
            >
              <option value="">Alle</option>
              {STATUSSEN.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </label>
          <div className="space-y-2">
            {punten.map((p) => (
              <button
                className="block w-full text-left border rounded p-3"
                key={p.id}
                onClick={() => setParams({ id: p.id })}
              >
                <strong>{p.titel}</strong>
                <div className="text-sm">
                  P{p.prioriteit} · {CATEGORIEEN[p.categorie]} · {p.status} ·{" "}
                  {p.agent_meldingen?.[0]?.count || 0} meldingen ·{" "}
                  {p.eigenaar || "Nog geen eigenaar"}
                </div>
                <div className="text-sm text-slate-600">{p.terugkoppeling}</div>
              </button>
            ))}
          </div>
          <div className="flex gap-4">
            <button disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
              Vorige
            </button>
            <span>
              {totaal} punten · pagina {page + 1}
            </span>
            <button
              disabled={(page + 1) * 30 >= totaal}
              onClick={() => setPage((p) => p + 1)}
            >
              Volgende
            </button>
          </div>
        </>
      ) : (
        <>
          <button onClick={() => setParams({})}>← Werkvoorraad</button>
          {punt && (
            <div className="space-y-4">
              <h2 className="font-semibold">{punt.titel}</h2>
              <fieldset
                disabled={!admin || busy || !enabled}
                className="space-y-3"
              >
                <label>
                  Status{" "}
                  <select
                    className="border p-2"
                    value={punt.status}
                    onChange={(e) =>
                      setPunt({
                        ...punt,
                        status: e.target.value as Verbeterpunt["status"],
                      })
                    }
                  >
                    {[
                      opgeslagenStatus.current,
                      ...VOLGENDE[opgeslagenStatus.current],
                    ].map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </label>
                <label className="ml-3">
                  Prioriteit{" "}
                  <select
                    value={punt.prioriteit}
                    onChange={(e) =>
                      setPunt({ ...punt, prioriteit: Number(e.target.value) })
                    }
                  >
                    {[0, 1, 2, 3].map((n) => (
                      <option key={n} value={n}>
                        P{n}
                      </option>
                    ))}
                  </select>
                </label>
                {field("eigenaar", "Eigenaar")}
                {PLANVELDEN.map((k) => (
                  <label className="block" key={k}>
                    {k}
                    <textarea
                      className="block w-full border rounded p-2"
                      rows={2}
                      value={punt.plan[k] || ""}
                      onChange={(e) =>
                        setPunt({
                          ...punt,
                          plan: { ...punt.plan, [k]: e.target.value },
                        })
                      }
                    />
                  </label>
                ))}
                {field(
                  "live_ref",
                  "Live-bewijs (deployment/commit + controle)",
                )}
                {field(
                  "controle_ref",
                  "Bewijs uit een nieuw gecontroleerd geval",
                )}
                {field("terugkoppeling", "Terugkoppeling aan melders")}
                <button
                  className="bg-indigo-600 text-white rounded px-3 py-2"
                  onClick={() => void mutate({ ...punt, soort: "punt" })}
                >
                  Opslaan
                </button>
                <button
                  className="ml-3 underline"
                  disabled={!["nieuw", "onderzoek"].includes(punt.status)}
                  onClick={() =>
                    void mutate(
                      { id: punt.id, versie: punt.versie, action: "analyse" },
                      "POST",
                    )
                  }
                >
                  Analysevoorstel maken
                </button>
              </fieldset>
              {punt.analyse && (
                <section className="bg-indigo-50 rounded p-3 space-y-2">
                  <h3 className="font-semibold">
                    AI-voorstel, nog niet vastgesteld
                  </h3>
                  {PLANVELDEN.map((k) => (
                    <p key={k}>
                      <strong>{k}: </strong>
                      {punt.analyse?.[k]}
                    </p>
                  ))}
                  {admin && (
                    <button
                      disabled={busy || !enabled}
                      className="underline"
                      onClick={() =>
                        setPunt({ ...punt, plan: { ...punt.analyse! } })
                      }
                    >
                      Overnemen om te bewerken
                    </button>
                  )}
                </section>
              )}
              <h3 className="font-semibold">Klantgevallen</h3>
              {meldingen.map((m) => (
                <section className="border rounded p-3 space-y-2" key={m.id}>
                  <div>
                    {m.kanaal} · gemeld door {m.melder}
                  </div>
                  <p>{m.verwachting}</p>
                  {m.kanaal === "mail" &&
                  typeof m.context.conversation_id === "string" ? (
                    <Link
                      className="underline"
                      to={`/inbox?thread=${m.context.conversation_id}`}
                    >
                      Open mail
                    </Link>
                  ) : (
                    <button
                      className="underline"
                      onClick={() =>
                        useCallDrawerStore.getState().open(m.bron_id)
                      }
                    >
                      Open gesprek
                    </button>
                  )}
                  <details>
                    <summary>Context bij melden</summary>
                    <pre className="whitespace-pre-wrap break-words text-xs">
                      {JSON.stringify(m.context, null, 2)}
                    </pre>
                  </details>
                  <select
                    disabled={busy || !enabled}
                    className="border p-2"
                    value={m.klant_status}
                    onChange={(e) =>
                      setMeldingen((ms) =>
                        ms.map((x) =>
                          x.id === m.id
                            ? { ...x, klant_status: e.target.value }
                            : x,
                        ),
                      )
                    }
                  >
                    {["open", "opgepakt", "afgehandeld"].map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                  <textarea
                    aria-label="Afhandeling klantgeval"
                    disabled={busy || !enabled}
                    className="block w-full border p-2"
                    value={m.klant_notitie}
                    onChange={(e) =>
                      setMeldingen((ms) =>
                        ms.map((x) =>
                          x.id === m.id
                            ? { ...x, klant_notitie: e.target.value }
                            : x,
                        ),
                      )
                    }
                  />
                  <button
                    disabled={busy || !enabled}
                    className="underline"
                    onClick={() =>
                      void mutate({
                        soort: "melding",
                        id: m.id,
                        versie: m.versie,
                        klant_status: m.klant_status,
                        klant_notitie: m.klant_notitie,
                      })
                    }
                  >
                    Klantstatus bewaren (voert niets uit)
                  </button>
                  {admin && enabled && (
                    <BundelMelding melding={m} busy={busy} save={mutate} />
                  )}
                </section>
              ))}
              <details>
                <summary>Historie</summary>
                {historie.map((h) => (
                  <p key={h.id}>
                    {new Date(h.created_at).toLocaleString("nl-NL")} · {h.actor}{" "}
                    · {h.nieuw.status} · {h.nieuw.terugkoppeling}
                  </p>
                ))}
              </details>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function BundelMelding({
  melding,
  busy,
  save,
}: {
  melding: Melding;
  busy: boolean;
  save: (b: Record<string, unknown>) => Promise<void>;
}) {
  const [zoek, setZoek] = useState(""),
    [keuze, setKeuze] = useState(""),
    [opties, setOpties] = useState<Verbeterpunt[]>([]),
    [error, setError] = useState("");
  async function zoekPunten() {
    try {
      const d = await verbeterApi("?open=1&zoek=" + encodeURIComponent(zoek));
      setOpties(
        d.punten.filter((p: Verbeterpunt) => p.id !== melding.verbeterpunt_id),
      );
      setError("");
    } catch (e) {
      setError(String(e));
    }
  }
  return (
    <details>
      <summary>Deze melding bij een ander verbeterpunt bundelen</summary>
      <p className="text-xs">
        Klantstatus en context blijven behouden. De status van het
        oorspronkelijke verbeterpunt verandert niet.
      </p>
      <input
        aria-label="Zoek verbeterpunt"
        className="border p-2"
        value={zoek}
        onChange={(e) => setZoek(e.target.value)}
      />
      <button
        className="underline ml-2"
        disabled={busy}
        onClick={() => void zoekPunten()}
      >
        Zoeken
      </button>
      <select
        aria-label="Doelpunt"
        className="border p-2"
        value={keuze}
        onChange={(e) => setKeuze(e.target.value)}
      >
        <option value="">Kies een punt</option>
        {opties.map((p) => (
          <option key={p.id} value={p.id}>
            {p.titel}
          </option>
        ))}
      </select>
      <button
        className="underline ml-2"
        disabled={busy || !keuze}
        onClick={() =>
          void save({
            soort: "bundel",
            id: melding.id,
            versie: melding.versie,
            punt_id: keuze,
          })
        }
      >
        Bundelen
      </button>
      {error && <p role="alert">{error}</p>}
    </details>
  );
}
